import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { DateTime } from 'luxon';
import { MailEventLog, MailEventLogType } from '../../eventlogs/MailEventlog.js';
import { workshopInfoEmail } from '../../mailer/mailTemplates.js';
import { chancenWerkMailer } from '../../mailer/server/mailer.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { ChancenPermission } from '../../permissions.js';
import { WorkshopAppointment } from '../entities/WorkshopAppointment.js';

export const sendWorkshopParticipationEmail = registerQuery({
  type: () => Boolean,
  operationName: 'sendWorkshopParticipationEmail',
  resolve: (appointmentID: string, infos: string, userIDs: string[]) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user?.hasPermission(ChancenPermission.raum.EDIT_WORKSHOP)) {
        throw new Error('No permission');
      }

      const appointment = await WorkshopAppointment.getByID<WorkshopAppointment>(appointmentID)({
        ...WorkshopAppointment.allFields,
        workshop: {
          title: 1,
          appointmentInfo: 1,
        },
      });
      const users = (await loadMongoData(ChancenwerkUser, userIDs, {
        projection: {
          vorname: 1,
          nachname: 1,
          email: 1,
        },
      })) as EntityData<ChancenwerkUser>[];

      if (!appointment || users.length === 0) {
        throw new Error('Appointment or users not found');
      }

      for (const user of users) {
        try {
          const msg = await Message.compose({
            subject: 'CHANCENakademie: Workshop Informationen',
            html: workshopInfoEmail({
              name: `${user.vorname} ${user.nachname}`,
              workshopName: appointment.workshop?.title ?? '',
              date: appointment.start ?? DateTime.now(),
              end: appointment.end ?? DateTime.now(),
              infos: infos,
            }),
          });
          await chancenWerkMailer.sendMail(msg, new Recipient(user.email));
          await new MailEventLog({
            userID: user._id,
            mailType: MailEventLogType.WORKSHOP_INFO,
            successful: true,
            sendMailError: undefined,
            emailAddress: user.email,
          }).create();
        } catch (e: any) {
          await new MailEventLog({
            userID: user._id,
            mailType: MailEventLogType.WORKSHOP_INFO,
            successful: false,
            sendMailError: e.message,
            emailAddress: user.email,
          }).create();
        }
      }
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'appointmentID' },
    { type: () => String, name: 'infos' },
    { type: () => [String], name: 'userIDs' },
  ],
});
